// @ts-check
// =============================================================================
// js/app/session.js — identity, login, tiers, and OAuth re-auth.
//
// WHAT THIS IS
//   The cross-cutting identity layer (plan/auth-and-tiers.md). It owns:
//     - resolving WHO the player is: ?reference / ?REFERENCE: param,
//       localStorage.reference, or a "JustAGuest<timestamp>" fallback,
//     - POST /login + unpacking its payload into the store,
//     - the tier derivation (guest / cheap / paid) + the can() gating helper,
//     - the OAuth provider list + re-auth redirects for expired sessions.
//
// LOGIN FLOW (mirrors the old client :290–470)
//   ensureSession() → found a reference? log in with it.
//                   → none? arm a 3s timer; if nothing logs in by then,
//                     auto-login as a guest so the game is always playable.
//   Successful login stores userData/cards/heros/lobby, persists the
//   reference (non-guests), connects the socket, and sets session.phase='ready'.
//
// SILENT RE-LOGIN (docs/RESTART_GRACE.md phase 2 + appendix)
//   A server restart wipes the in-RAM token table, so the first socket
//   reconnect gets 'Session Expired'. socket.js OWNS the permission gate
//   (announced pause / recent server contact / visible tab, plus the attempt
//   cap) and only invokes the handler we register with it (a direct import
//   the other way would be circular) when a silent re-login is allowed. Our
//   half is identity + login: prefer the LIVE session's reference
//   (store.session.reference — /login echoes it back for accounts and guests
//   alike) over re-reading storage, because private-mode browsers fail
//   storage reads or mint a FRESH guest identity, which would orphan the
//   snapshotted duel and re-login as a stranger; then re-run loginToServer
//   with { silent: true } so a transport failure never bounces session.phase
//   back and flashes the loading screen; the socket's reconnect/poll
//   machinery just keeps running.
// =============================================================================

import { store, setSlice, updateSlice } from '../core/store.js';
import { login as httpLogin, setUnverifiedHandler } from '../net/http.js';
import { connectSocket, setSessionExpiredHandler } from '../net/socket.js';
import { openModal } from '../ui/modal.js';
import { invalidateImageCacheIfStale } from '../render/image-cache.js';
import { isQuickplay } from './quickplay-mode.js';
import { isShell, openFullSiteTab } from './shell-mode.js';
import { loginLinkProvider, withoutLoginParam } from './login-link.js';
import { callStatsAPI, trafficSource } from './analytics.js';

// -----------------------------------------------------------------------------
// OAuth providers (ported from the old client :176–192 — these URLs point at
// the live Cloud Function callbacks that mint the ?reference).
//
// The `state` param tells the Cloud Function callback which domain to send the
// player BACK to after auth, so it must match the host we're actually on:
//   dev.deckbiter.cards         -> devdeckbiter
//   placeholder.deckbiter.cards -> placeholderdeckbiter
//   anything else               -> deckbiter   (the production fallback)
// Add more hosts here as needed (and register their state with the callback).
// -----------------------------------------------------------------------------
const OAUTH_STATE_BY_HOST = {
	'dev.deckbiter.cards': 'devdeckbiter',
	'placeholder.deckbiter.cards': 'placeholderdeckbiter',
};
const OAUTH_STATE =
	OAUTH_STATE_BY_HOST[typeof window !== 'undefined' ? window.location.hostname : ''] ||
	'deckbiter';

export const LOGIN_PROVIDERS = [
	{
		label: 'SubscribeStar',
		color: '#00aff0',
		url: `https://subscribestar.art/oauth2/authorize?client_id=2iiUcUFavnLR9T2KrbBnx3Tgd7Bmx9rrbOnMO2IibmU&redirect_uri=https%3A%2F%2Fus-west4-fffme-345705.cloudfunctions.net%2Fcallback&response_type=code&scope=subscriber.read+user.read&state=${OAUTH_STATE}`,
	},
	{
		label: 'Patreon',
		color: '#ff424d',
		url: `https://www.patreon.com/oauth2/authorize?response_type=code&client_id=2QSkps0iX0gmBwzMV7HxVYLHeahavlH1Kgp003kaa9Agpm7oqtj-KLOUjqJYVrr3&redirect_uri=https://us-west4-fffme-345705.cloudfunctions.net/patreon&scope=identity%20identity.memberships&state=${OAUTH_STATE}`,
	},
	{
		label: 'Discord',
		color: '#5865f2',
		url: `https://discord.com/oauth2/authorize?client_id=1062986589992661002&redirect_uri=https://us-west4-fffme-345705.cloudfunctions.net/plusone&response_type=code&scope=identify&state=${OAUTH_STATE}`,
		// Hidden from the login picker FOR NOW: new logins go through Patreon
		// or SubscribeStar. Kept in the list so existing Discord_ accounts can
		// still re-auth (reauthRedirect looks providers up here).
		hidden: true,
	},
];

/** Which provider owns an account id, by its prefix (old client :252). */
const PROVIDER_BY_PREFIX = [
	{ prefix: 'Star_', label: 'SubscribeStar' },
	{ prefix: 'Patreon_', label: 'Patreon' },
	{ prefix: 'Discord_', label: 'Discord' },
];

/**
 * The easiest path to a FREE membership — Patreon's "how to play for free" post.
 * EVERY "Join / become a member" CTA leads here (splash, header, deck-builder
 * lock, and the free-feature upsells). The ONLY place we send players to the
 * paid membership page instead is the rewards screen's pack-opening upsell.
 */
export const JOIN_URL = 'https://www.patreon.com/smuttygames/posts/play-games-124514582';

/**
 * Open the "Log in with …" provider picker modal (old ShowLoginOptions :194).
 * @returns {void}
 */
export function showLoginOptions() {
	// Shell (itch etc.): OAuth can't run in a portal shell — providers refuse to
	// render, and a same-window redirect would strand the frame. Hand off to
	// a real tab on the full site instead. (Login BUTTONS are already hidden
	// when embedded; this guards every other caller.)
	if (isShell()) {
		const body = document.createElement('div');
		const note = document.createElement('p');
		note.textContent = 'Accounts live on the full site — open it in a new tab, log in there, and your collection follows you.';
		body.appendChild(note);
		const open = document.createElement('button');
		open.className = 'provider-btn';
		open.textContent = 'Open Deckbiter ↗';
		open.addEventListener('click', openFullSiteTab);
		body.appendChild(open);
		const cancel = document.createElement('button');
		cancel.className = 'cancel-btn';
		cancel.textContent = 'Cancel';
		body.appendChild(cancel);
		const box = openModal({ title: 'Log in on the full site', body });
		cancel.addEventListener('click', () => box.closest('.modal-overlay')?.remove());
		return;
	}
	const body = document.createElement('div');
	for (const provider of LOGIN_PROVIDERS.filter((p) => !p.hidden)) {
		const btn = document.createElement('button');
		btn.className = 'provider-btn';
		btn.textContent = provider.label;
		btn.style.background = provider.color;
		btn.addEventListener('click', () => {
			window.location.href = provider.url;
		});
		body.appendChild(btn);
	}
	const cancel = document.createElement('button');
	cancel.className = 'cancel-btn';
	cancel.textContent = 'Cancel';
	body.appendChild(cancel);
	const box = openModal({ title: 'Log in with', body });
	cancel.addEventListener('click', () => box.closest('.modal-overlay')?.remove());
}

/**
 * Send an expired/unverified player back through the SAME OAuth flow the
 * picker uses (old ReauthRedirect :273): straight to their provider when the
 * account prefix identifies it, else open the picker.
 * @param {string} accountId
 * @returns {void}
 */
export function reauthRedirect(accountId) {
	const id = String(accountId || '');
	for (const { prefix, label } of PROVIDER_BY_PREFIX) {
		if (id.includes(prefix)) {
			const provider = LOGIN_PROVIDERS.find((p) => p.label === label);
			if (provider) {
				window.location.href = provider.url;
				return;
			}
		}
	}
	showLoginOptions();
}

// -----------------------------------------------------------------------------
// Tier gating — the ONE place screens ask "may I?" (auth-and-tiers.md).
// -----------------------------------------------------------------------------

/**
 * What each feature requires. Guests are blocked from anything server-side
 * that needs a persisted account (hosting, deck saving, pack earning); bot
 * duels and settings work statelessly, so guests get those too.
 * @type {Record<string, Array<'guest'|'cheap'|'paid'>>}
 */
const FEATURE_TIERS = {
	saveDeck: ['cheap', 'paid'],
	deckbuild: ['cheap', 'paid'],
	host: ['cheap', 'paid'],
	// Guests may fight the bot (prebuilt decks only — the server enforces it)
	// and set their content/gender filters. No accounts needed to try the game.
	duelBot: ['guest', 'cheap', 'paid'],
	earnPacks: ['cheap', 'paid'],
	openPack: ['paid'],
	settings: ['guest', 'cheap', 'paid'],
	join: ['guest', 'cheap', 'paid'],
	spectate: ['guest', 'cheap', 'paid'],
	// In-duel chat: paid accounts only (the server re-enforces this).
	chat: ['paid'],
};

/**
 * May the current player use a feature?
 * @param {keyof typeof FEATURE_TIERS | string} feature
 * @returns {boolean}
 */
export function can(feature) {
	const allowed = FEATURE_TIERS[feature];
	if (!allowed) {
		console.error(`can("${feature}") — unknown feature, denying`);
		return false;
	}
	return allowed.includes(store.session.tier);
}

/**
 * Derive the tier from a login payload. The server sets `tier` directly
 * (decision #9); older blobs fall back to the premium flags.
 * @param {any} userData
 * @returns {'guest'|'cheap'|'paid'}
 */
function deriveTier(userData) {
	if (userData.tier === 'guest' || userData.JustAGuest) return 'guest';
	if (userData.tier === 'paid' || userData.realUser || userData.premium) return 'paid';
	return 'cheap';
}

// -----------------------------------------------------------------------------
// The login flow itself.
// -----------------------------------------------------------------------------

/** Guards the guest auto-login timer against double-firing. */
let autoGuestArmed = false;

/**
 * Kick off session resolution at boot (AFTER the 18+ gate accepts).
 *
 * Order of precedence (old SeeIfHasAccountAlready :290):
 *   1. ?reference= / ?REFERENCE: in the URL (fresh OAuth callback),
 *   2. localStorage.reference (returning player),
 *   3. nothing → arm the 3s guest auto-login.
 * @returns {void}
 */
export function ensureSession() {
	if (store.session.phase === 'ready' || store.session.phase === 'login') return;
	updateSlice('session', { phase: 'login' });

	// Shell play is GUEST-ONLY (shell-mode.js): skip reference resolution
	// entirely — a stored login could only lead to account flows (re-auth
	// redirects, upsells) that cannot work inside an iframe.
	if (isShell()) {
		armGuestAutoLogin();
		return;
	}

	// Accept BOTH reference formats the site can receive (the OAuth callbacks
	// send "?REFERENCE:<ref>"; plain links use "?reference=<ref>").
	const refMatch = window.location.search.match(/reference[:=]([^&]+)/i);
	let reference = null;
	if (refMatch) {
		try {
			reference = decodeURIComponent(refMatch[1]);
		} catch {
			reference = refMatch[1];
		}
		// Scrub the secret reference out of the address bar + history.
		const url = new URL(String(window.location));
		url.search = '';
		window.history.pushState({}, document.title, url);
	} else {
		reference = localStorage.getItem('reference');
	}

	// "?login=patreon" (the Patreon guide's Play button): a player with no
	// login goes straight through that provider's OAuth; one already logged
	// in just gets the param tidied away. Shells never reach here (above).
	const linked = loginLinkProvider(window.location.search, LOGIN_PROVIDERS);
	if (linked && !reference) {
		window.location.href = linked.url;
		return;
	}
	if (linked) {
		window.history.replaceState({}, document.title, withoutLoginParam(String(window.location)));
	}

	if (reference) {
		loginToServer(reference);
	} else {
		armGuestAutoLogin();
	}
}

/**
 * If nothing has logged in within 3 seconds, play as a guest (old :465).
 * Quickplay and shell (portal) play barely wait: both are guest-only
 * funnels, and with no stored reference there is nothing to wait FOR.
 * @returns {void}
 */
function armGuestAutoLogin() {
	if (autoGuestArmed) return;
	autoGuestArmed = true;
	setTimeout(() => {
		if (!store.userData) {
			loginToServer(guestReference());
		}
	}, isQuickplay() || isShell() ? 250 : 3000);
}

/** A recently-used guest identity expires after this long idle. */
const GUEST_REFERENCE_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * The guest's sticky identity. The server derives the guest's player id from
 * this string, so reusing it across reloads keeps the guest the SAME player —
 * which is what lets them fall back into a live duel after a refresh or a
 * server restart. Minted with a crypto-random suffix (the string doubles as
 * the guest's credential, so it must be unguessable) and kept in localStorage
 * with a sliding TTL: active guests keep their identity, stale ones start
 * fresh.
 * @returns {string}
 */
function guestReference() {
	try {
		const stored = JSON.parse(localStorage.getItem('guestReference') || 'null');
		if (stored?.ref && Date.now() - stored.at < GUEST_REFERENCE_TTL_MS) {
			localStorage.setItem('guestReference', JSON.stringify({ ref: stored.ref, at: Date.now() }));
			return stored.ref;
		}
	} catch {
		// unreadable storage — mint a fresh identity below
	}
	const suffix = Array.from(crypto.getRandomValues(new Uint8Array(10)), (b) => b.toString(16).padStart(2, '0')).join('');
	const ref = 'JustAGuest' + Date.now() + suffix;
	try {
		localStorage.setItem('guestReference', JSON.stringify({ ref, at: Date.now() }));
		// A genuinely-new identity (expired/absent guestReference) starts with a
		// fresh name too — so the nickname resets exactly "on your next visit"
		// (profile note), not on every reload.
		localStorage.removeItem('guestNickname');
	} catch {
		// private-mode storage quota — the guest just won't survive a reload
	}
	return ref;
}

/**
 * A guest's settings live in THEIR browser (the server has no DB row for
 * them): stored after every settings save, replayed to /login so the server
 * derives the art collection from them. The game defaults to ZERO censorship
 * (the 18+ gate already vouches for adult content) — matching the server's
 * settings template — so a first-time guest sees everything and can dial
 * content back up in Settings if they want. Owning art is separate from seeing
 * it: guests still can't put un-earned art in a deck (server-enforced on save).
 * @returns {object}
 */
export function guestSettings() {
	try {
		const stored = JSON.parse(localStorage.getItem('guestSettings') || 'null');
		if (stored && typeof stored === 'object') return stored;
	} catch {
		// unreadable storage — fall through to the uncensored defaults
	}
	return { censor: { nude: false, smut: false, vore: false, gore: false } };
}

/**
 * A guest's nickname lives in THEIR browser (no DB row), replayed to /login so
 * the server reuses it instead of minting a fresh random name every load — the
 * cause of the "name keeps changing" report. Tied to the guestReference
 * lifecycle: minting a new identity clears it (see guestReference), so the name
 * is stable across reloads but genuinely resets on a new visit.
 * @returns {string|null}
 */
export function guestNickname() {
	try {
		return localStorage.getItem('guestNickname') || null;
	} catch {
		return null;
	}
}

/**
 * Remember a guest's nickname for the next login (the minted one on first visit,
 * or one they set via the profile rename). No-op for storage-blocked browsers.
 * @param {string} name
 * @returns {void}
 */
export function rememberGuestNickname(name) {
	if (!name) return;
	try {
		localStorage.setItem('guestNickname', name);
	} catch {
		// private-mode quota — the name just won't survive a reload
	}
}

/**
 * POST /login and unpack the result into the store (old LoginToServer :328).
 * @param {string} entry  A reference or "JustAGuest<timestamp>" string.
 * @param {{silent?: boolean}} [opts]  silent: this is a mid-session re-login
 *        ('Session Expired' after a server restart). On transport failure it
 *        gives up quietly — no session.phase reset (which would flash the
 *        loading screen), no guest auto-login — and leaves the socket's
 *        reconnect/poll machinery to trigger the next attempt.
 * @returns {Promise<void>}
 */
/**
 * Full-screen "server at capacity" notice with a Try-Again button (guest caps).
 * Reuses the reboot-takeover styling; builds text with textContent (no innerHTML).
 * @param {string} [reason]
 * @returns {void}
 */
function showCapacityNotice(reason) {
	if (document.querySelector('.capacity-takeover')) return;
	const el = document.createElement('div');
	el.className = 'reboot-takeover capacity-takeover';
	const box = document.createElement('div');
	box.style.cssText = 'max-width:32rem;padding:1.25rem;text-align:center;';
	const title = document.createElement('div');
	title.style.cssText = 'font-size:1.25rem;margin-bottom:0.75rem;font-weight:bold;';
	title.textContent = 'Server At Capacity';
	const body = document.createElement('div');
	body.style.cssText = 'margin-bottom:1.25rem;line-height:1.5;';
	body.textContent = reason || 'The server is at capacity right now. Please try again shortly.';
	const retry = document.createElement('button');
	retry.className = 'defaultButton';
	retry.textContent = 'Try Again';
	retry.addEventListener('click', () => window.location.reload());
	box.append(title, body, retry);
	el.appendChild(box);
	document.body.appendChild(el);
}

export async function loginToServer(entry, { silent = false } = {}) {
	const isGuest = entry.includes('JustAGuest');
	// A ?guest=<code> invite lets this login borrow the host's decks.
	const guestCode = new URLSearchParams(window.location.search).get('guest') || 'none';

	let userData;
	try {
		userData = await httpLogin(entry, guestCode, isGuest ? guestSettings() : null, isGuest ? guestNickname() : null);
	} catch (error) {
		console.error('Login failed:', error);
		if (silent) {
			// Mid-session re-login: the session is still 'ready' and the page is
			// live — don't touch the phase (that flashes the loading screen) and
			// don't arm the guest fallback. The reconnect/poll machinery retries.
			return;
		}
		// Server unreachable: fall back to guest (or retry) via the timer.
		updateSlice('session', { phase: 'gate' });
		armGuestAutoLogin();
		return;
	}

	if (userData.atCapacity) {
		// The server declined a NEW guest session (global or per-IP cap).
		// SILENT re-logins settle quietly instead (wire contract: "never a
		// takeover"): a restart wiped loadedUsers, so a reconnecting guest
		// near capacity would otherwise get a full-page notice over a live —
		// possibly snapshot-restored — duel with no user action behind it.
		// The orb stays red; a manual reload gets the loud answer.
		if (silent) return;
		// Show the notice and STOP — do not re-arm the guest auto-login (no
		// retry loop).
		showCapacityNotice(userData.reason);
		return;
	}

	if (userData.unverified) {
		// Same rule for a dead reference: a background event must not clear
		// storage, alert(), and navigate the tab away mid-game. Leave the
		// stored reference for the next DELIBERATE load to resolve loudly.
		if (silent) return;
		// Bad/expired token: forget it and go back through OAuth.
		localStorage.removeItem('reference');
		alert('Please log back in.');
		reauthRedirect(entry);
		return;
	}

	// --- Unpack the payload into the store --------------------------------
	if (isGuest) {
		// The server already applied the settings we replayed (guestSettings():
		// their stored filters, or the uncensored defaults for a first visit)
		// and derived the collection from them — the payload is authoritative.
		userData.JustAGuest = true;
		// Persist the name for next load: the one we replayed, or the fresh
		// random one the server minted on a first visit. Keeps it stable.
		rememberGuestNickname(userData.nickname);
	}

	setSlice('userData', userData);
	setSlice('cards', userData.cards || []);
	setSlice('heros', userData.heros || []);
	setSlice('lobby', { duels: userData.duels || [], deets: userData.deets || {} });
	setSlice('unopenedPacks', (userData.collection?.packs || []).length);

	// VERSION is the art cache-buster: a bump invalidates the IndexedDB cache.
	invalidateImageCacheIfStale(userData.VERSION);
	setSlice('version', userData.VERSION);
	// CDN offload base ('' => disabled). Read by js/net/cdn.js to route card art.
	setSlice('cdnBase', userData.cdnBase || '');

	// Remember the selected deck across visits (old :438).
	const savedDeckIndex = localStorage.getItem('Deck Index');
	if (savedDeckIndex !== null && userData.decks?.[savedDeckIndex]) {
		setSlice('loadedDeck', userData.decks[savedDeckIndex]);
	}

	// Returning players stay logged in across visits. Guests never get a
	// 'reference' — their sticky identity lives under 'guestReference' above.
	if (!isGuest) {
		localStorage.setItem('reference', userData.reference);
	}

	const tier = deriveTier(userData);
	updateSlice('session', {
		phase: 'ready',
		tier,
		reference: userData.reference,
		token: userData.token,
		name: userData.name,
		nickname: userData.nickname,
		isGuest,
	});

	// The visit ping + tier breakdown, in the same post-login spot the old
	// client sent them (OldClientReference/index.js :356) — this was never
	// ported, so the dashboard's Init count flatlined the moment the old
	// client left the domain. callStatsAPI dedups per browser for 20h, so
	// reloads don't inflate anything. Dashboard keys stay continuous with
	// the old data: guests count as None (old freeUser semantics), paid as
	// FourDollarTier; the middle tier gets its own bucket.
	callStatsAPI('Init');
	callStatsAPI('Subscription:' + (tier === 'paid' ? 'FourDollarTier' : tier === 'cheap' ? 'CheapTier' : 'None'));
	// Channel attribution: portal wrappers (?portal=itch etc.), unknown
	// iframes, the quickplay link, and direct visits each get their own
	// dashboard bucket (analytics.js trafficSource).
	callStatsAPI('Source:' + trafficSource());

	// Open the realtime channel now that we have credentials.
	connectSocket(userData.name, userData.token);
}

// When /request says "unverified" mid-session, run the same re-auth flow.
setUnverifiedHandler(() => {
	localStorage.removeItem('reference');
	alert('Please log back in.');
	reauthRedirect(store.session.name || '');
});

// -----------------------------------------------------------------------------
// Silent re-login on 'Session Expired' (see the header's SILENT RE-LOGIN note).
// The server emits it right before hanging up on a socket whose token no
// longer verifies — the normal state right after a restart. socket.js has
// already run the permission gate + attempt cap by the time this fires.
// Re-minting the token through the ordinary login path replaces the socket,
// and the server's connect handler re-delivers lobby + any restored duel on
// its own.
// -----------------------------------------------------------------------------

/** One re-login in flight at a time — 'Session Expired' can repeat while the
 *  server flaps, and overlapping logins would race each other's sockets. */
let reloginInFlight = false;

setSessionExpiredHandler(async () => {
	if (reloginInFlight) return;
	// A superseded tab staying kicked is correct: its takeover offers the
	// explicit "Play" reclaim. (socket.js also guards this; belt + suspenders
	// because a re-login from under the takeover would steal the session back.)
	if (document.querySelector('.superseded-takeover')) return;
	// Before the first login resolves there is nothing to re-login AS.
	if (store.session.phase !== 'ready') return;
	// Identity: the LIVE session's reference first — the exact string /login
	// echoed back at boot, for accounts AND guests alike. Storage is only the
	// fallback: private-mode browsers fail storage reads or mint a fresh guest
	// (guestReference's TTL/error path), which would orphan the snapshotted
	// duel and re-login as a stranger.
	const entry = store.session.reference
		|| (store.session.isGuest ? guestReference() : localStorage.getItem('reference'));
	if (!entry) return;
	reloginInFlight = true;
	try {
		await loginToServer(entry, { silent: true });
	} catch (error) {
		// A failed attempt just leaves the poll/reconnect machinery running —
		// the next 'Session Expired' (or the user reloading) tries again.
		console.warn('Silent re-login failed:', error);
	} finally {
		reloginInFlight = false;
	}
});
